import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormHelperText
} from '@mui/material';
import PropTypes from 'prop-types';

/**
 * ProductPersonalization component
 * 
 * This component renders a personalization text field for products that support personalization.
 * It handles three states: not shown, optional, and required.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.product - The product object
 * @param {string} props.value - Current personalization text value
 * @param {Function} props.onChange - Callback when personalization text changes
 * @returns {JSX.Element|null} The rendered component or null if personalization is not enabled
 */
const ProductPersonalization = ({ product, value, onChange }) => {
  // If product doesn't have personalization or it's set to not_shown, don't render anything
  if (!product.personalization || product.personalization.enabled === 'not_shown') {
    return null;
  }

  const isRequired = product.personalization.enabled === 'required';
  const maxLength = product.personalization.maxLength || 250;
  const instructions = product.personalization.instructions || 'Add your personalization details';

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" gutterBottom>
        Personalization
        {!isRequired && (
          <Typography component="span" variant="body2" sx={{ ml: 1 }}>
            (Optional)
          </Typography>
        )}
      </Typography>
      
      {instructions && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {instructions}
        </Typography>
      )}
      
      <TextField
        label="Personalization"
        multiline
        rows={3}
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={isRequired}
        error={isRequired && !value}
        helperText={`${value ? value.length : 0}/${maxLength} characters`}
        inputProps={{ maxLength }}
      />
      
      {isRequired && !value && (
        <FormHelperText error>
          This field is required
        </FormHelperText>
      )}
    </Box>
  );
};

ProductPersonalization.propTypes = {
  product: PropTypes.object.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

ProductPersonalization.defaultProps = {
  value: ''
};

export default ProductPersonalization;
